import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Icon,
  Stack,
  TextField,
  Card,
  PortalWrapper,
} from 'mw-style-react';
import { useIntl } from 'hooks';
import mes from './intl';
import scss from './ContextualFormattingMenuPopup.scss';

const EDIT_MODE_MENU_WIDTH = 188;
const MENU_WIDTH = 44;

/**
 * A reusable component for displaying a quote popup when text is selected
 *
 * @param {object} props - Component props
 * @param {object} props.popupStyle - Style object for positioning the popup
 * @param {function} props.onQuote - Callback function when quote button is clicked
 * @param {object} props.popupRef - Ref object for the popup
 * @param {object} props.containerRef - Ref object for the container element
 * @return {React.ReactElement|null} - Quote popup component or null if not visible
 */
function ContextualFormattingMenuPopup({
  popupStyle,
  onQuote,
  onCreateActor,
  onFormat,
  popupRef,
  containerRef,
  visible,
  selectedText,
  clearSelection,
  isEditMode,
}) {
  const t = useIntl();
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const isTemporaryHideFormatingButtons = true;
  const isTemporaryHideCreateActorButton = true;

  if (!visible || !selectedText.text || !containerRef?.current) return null;

  const handleBold = () => {
    if (onFormat) {
      onFormat(`[b]${selectedText.text}[/b]`);
    }
    clearSelection();
  };

  const handleItalic = () => {
    if (onFormat) {
      onFormat(`[i]${selectedText.text}[/i]`);
    }
    clearSelection();
  };

  const handleLink = () => {
    if (showLinkForm && linkUrl) {
      const validUrl =
        linkUrl.startsWith('http://') || linkUrl.startsWith('https://')
          ? linkUrl
          : `https://${linkUrl}`;

      if (onFormat) {
        onFormat(`[url=${validUrl}]${selectedText.text}[/url]`);
      }
      setLinkUrl('');
      setShowLinkForm(false);
      clearSelection();
    } else {
      setShowLinkForm(true);
    }
  };

  const handleList = () => {
    const lines = selectedText.text.split('\n').filter((line) => line.trim());
    const listItems = lines.map((line) => `<li>${line.trim()}</li>`).join('');
    const formattedList = `<ol>${listItems}</ol>`;
    if (onFormat) {
      onFormat(formattedList);
    }
    clearSelection();
  };

  const handleQuote = () => {
    if (onQuote) {
      onQuote(selectedText);
    }
    clearSelection();
  };

  const handleCreateActorClick = () => {
    onCreateActor();
  };

  const handleCancelLink = () => {
    setShowLinkForm(false);
    setLinkUrl('');
  };

  const POPUP_MENU_WIDTH = isEditMode ? EDIT_MODE_MENU_WIDTH : MENU_WIDTH;

  if (isEditMode && isTemporaryHideFormatingButtons) return null;

  if (showLinkForm) {
    return (
      <PortalWrapper
        root={document.getElementById('mainRoot')}
        node={containerRef.current}
        width={POPUP_MENU_WIDTH}
        offsetX={popupStyle.left - POPUP_MENU_WIDTH / 2}
        offsetY={popupStyle.top}
        position="absolute"
      >
        <Card
          forwardRef={popupRef}
          className={scss.contextualMenu}
          withBorder={false}
        >
          <div className={scss.linkForm}>
            <Stack.V size="small">
              <TextField
                placeholder="Enter URL"
                value={linkUrl}
                onChange={({ value }) => setLinkUrl(value)}
                autoFocus
                size="small"
              />
              <Stack.H size="small" justifyContent="spaceBetween">
                <Button
                  size="small"
                  label="Cancel"
                  type="text"
                  onClick={handleCancelLink}
                />
                <Button
                  size="small"
                  label="Apply"
                  onClick={handleLink}
                  visibility={linkUrl ? 'visible' : 'disabled'}
                />
              </Stack.H>
            </Stack.V>
          </div>
        </Card>
      </PortalWrapper>
    );
  }

  return (
    <PortalWrapper
      root={document.getElementById('mainRoot')}
      node={containerRef.current}
      width={POPUP_MENU_WIDTH}
      offsetX={popupStyle.left - POPUP_MENU_WIDTH / 2}
      offsetY={popupStyle.top}
      position="absolute"
    >
      <Card
        forwardRef={popupRef}
        className={scss.contextualMenu}
        withBorder={false}
      >
        <Stack.H size="none">
          {!isEditMode && (
            <Button
              size="small"
              type="text"
              icon={<Icon type="quote" />}
              onClick={handleQuote}
              title={t(mes.quote)}
              className={scss.menuButton}
            />
          )}
          {!isTemporaryHideCreateActorButton && (
            <Button
              size="small"
              type="text"
              icon={<Icon type="add" />}
              onClick={handleCreateActorClick}
              title="Create Actor"
              className={scss.menuButton}
            />
          )}
          {isEditMode && (
            <>
              <Button
                size="small"
                type="text"
                icon={<strong>B</strong>}
                onClick={handleBold}
                title="Bold"
                className={scss.menuButton}
              />
              <Button
                size="small"
                type="text"
                icon={<em>I</em>}
                onClick={handleItalic}
                title="Italic"
                className={scss.menuButton}
              />
              <Button
                size="small"
                type="text"
                icon={<Icon type="link" />}
                onClick={handleLink}
                title="Link"
                className={scss.menuButton}
              />
              <Button
                size="small"
                type="text"
                icon={<Icon type="list_numbered" />}
                onClick={handleList}
                title="List"
                className={scss.menuButton}
              />
            </>
          )}
        </Stack.H>
      </Card>
    </PortalWrapper>
  );
}

ContextualFormattingMenuPopup.propTypes = {
  popupStyle: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
  }).isRequired,
  onQuote: PropTypes.func.isRequired,
  onCreateActor: PropTypes.func,
  onFormat: PropTypes.func,
  popupRef: PropTypes.object.isRequired,
  containerRef: PropTypes.object.isRequired,
  visible: PropTypes.bool.isRequired,
  selectedText: PropTypes.shape({
    text: PropTypes.string,
  }).isRequired,
  clearSelection: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
};

export default ContextualFormattingMenuPopup;
